import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldCheckIcon } from 'lucide-react';
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}
interface CookieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: CookiePreferences) => void;
}
export function CookieSettings({
  isOpen,
  onClose,
  onSave
}: CookieSettingsProps) {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const saved = localStorage.getItem('vitafrukt-cookie-consent');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences({
          necessary: true,
          analytics: parsed.analytics ?? false,
          marketing: parsed.marketing ?? false
        });
      } catch {}
    }
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSave = () => {
    onSave(preferences);
  };
  const handleAcceptAll = () => {
    onSave({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };
  const categories = [{
    key: 'necessary' as const,
    name: 'Notwendige Cookies',
    description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
    locked: true
  }, {
    key: 'analytics' as const,
    name: 'Statistik & Analyse',
    description: 'Helfen uns zu verstehen, wie Besucher mit der Website interagieren.',
    locked: false
  }, {
    key: 'marketing' as const,
    name: 'Marketing',
    description: 'Werden verwendet, um Besuchern relevante Inhalte anzuzeigen.',
    locked: false
  }];
  return <AnimatePresence data-id="element-97">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-98">
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" data-id="element-99" />

          {/* Modal */}
          <motion.div ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Cookie-Einstellungen" initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative bg-white rounded-softer shadow-vita-hover max-w-lg w-full p-6 md:p-8 z-10 max-h-[90vh] overflow-y-auto" data-id="element-100">
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-vita-text-light hover:text-vita-text transition-colors p-1" aria-label="Schließen" data-id="element-101">
              <XIcon className="w-5 h-5" data-id="element-102" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-2" data-id="element-103">
              <div className="w-10 h-10 rounded-full bg-vita-green-light flex items-center justify-center" data-id="element-104">
                <ShieldCheckIcon className="w-5 h-5 text-vita-green-deep" data-id="element-105" />
              </div>
              <h2 className="font-heading text-xl font-bold text-vita-text" data-id="element-106">
                Cookie-Einstellungen
              </h2>
            </div>
            <p className="text-vita-text-muted text-sm mb-6" data-id="element-107">
              Hier kannst du auswählen, welche Cookies du zulassen möchtest.
            </p>

            {/* Categories */}
            <div className="space-y-4 mb-8" data-id="element-108">
              {categories.map(cat => <div key={cat.key} className="flex items-start gap-4 p-4 rounded-soft border border-vita-border bg-vita-cream/50" data-id="element-109">
                  <div className="flex-grow min-w-0" data-id="element-110">
                    <p className="font-medium text-vita-text text-sm" data-id="element-111">
                      {cat.name}
                    </p>
                    <p className="text-vita-text-light text-xs mt-1 leading-relaxed" data-id="element-112">
                      {cat.description}
                    </p>
                  </div>
                  {/* Toggle */}
                  <button type="button" role="switch" aria-checked={preferences[cat.key]} aria-label={cat.name} disabled={cat.locked} onClick={() => {
              if (!cat.locked) {
                setPreferences(prev => ({
                  ...prev,
                  [cat.key]: !prev[cat.key]
                }));
              }
            }} className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-vita-green-sage focus:ring-offset-2 ${preferences[cat.key] ? 'bg-vita-green-deep' : 'bg-vita-border'} ${cat.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-113">
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${preferences[cat.key] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-114" />
                  </button>
                </div>)}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-115">
              <button onClick={handleSave} className="flex-1 px-6 py-3 bg-vita-green-deep text-white font-medium rounded-full hover:bg-vita-green-medium transition-colors text-sm" data-id="element-116">
                Auswahl speichern
              </button>
              <button onClick={handleAcceptAll} className="flex-1 px-6 py-3 bg-white text-vita-text font-medium rounded-full border border-vita-border hover:border-vita-green-sage transition-colors text-sm" data-id="element-117">
                Alle akzeptieren
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}